/**
 * A refusal the service gave, or the fact that it could not be reached. The distinction the contract
 * insists on: a refusal is an answer, and an unreachable service is not.
 */
export class Refusal extends Error {
	#code: string;
	get code() {
		return this.#code;
	}

	#status: number;
	get status() {
		return this.#status;
	}

	#details?: Record<string, unknown>;
	get details() {
		return this.#details;
	}

	#request?: string;
	/** The request the service's answer names (`req_…`), for support; none when no answer came */
	get request() {
		return this.#request;
	}

	#time = new Date();
	/** When this refusal was received or the call gave up */
	get time() {
		return this.#time;
	}

	constructor(code: string, message: string, status = 0, details?: Record<string, unknown>, request?: string) {
		super(message);
		this.name = 'Refusal';
		this.#code = code;
		this.#status = status;
		this.#details = details;
		this.#request = request;
	}

	/**
	 * Why the session no longer serves, as Accounts told the service (D26): `expired` and `revoked`, where
	 * signing in again helps, or `suspended`, where it does not
	 */
	get ended(): 'expired' | 'revoked' | 'suspended' | undefined {
		const ended = this.#details?.ended;
		return ended === 'expired' || ended === 'revoked' || ended === 'suspended' ? ended : undefined;
	}

	/** The service could not be reached, or could not reach the authority. Never a refusal and never an approval */
	get unreachable(): boolean {
		return this.#code === 'UNREACHABLE' || this.#code === 'UPSTREAM_UNAVAILABLE' || this.#status === 503 || this.#status === 502 || this.#status === 504 || this.#status === 429;
	}

	/**
	 * A change the service did not answer in time: it may have been made. The person is told to check
	 * before trying again, and what is shown is read again
	 */
	get uncertain(): boolean {
		return this.#details?.outcome === 'unknown';
	}

	/** There is no valid identity here. Signing in is the useful next step */
	get unauthenticated(): boolean {
		return this.#code === 'UNAUTHENTICATED' || this.#code === 'AUDIENCE_REJECTED';
	}

	static #reasons: Record<string, 'expired' | 'revoked' | 'suspended'> = { SESSION_EXPIRED: 'expired', SESSION_REVOKED: 'revoked', ACCOUNT_SUSPENDED: 'suspended' };

	/**
	 * The session this request was made with no longer serves (`UNAUTHENTICATED`, a session Accounts
	 * says `ended`, or Accounts' own `SESSION_EXPIRED`, `SESSION_REVOKED` and `ACCOUNT_SUSPENDED`): the
	 * request was not carried out, and `Session` decides what happens next
	 */
	get lost(): boolean {
		return !this.quiet && (this.unauthenticated || this.ended !== undefined || this.#code in Refusal.#reasons);
	}

	/** Why the session ended, when the answer said so */
	get reason(): 'expired' | 'revoked' | 'suspended' | null {
		return this.ended ?? Refusal.#reasons[this.#code] ?? null;
	}

	/**
	 * A request not sent because the session ended and was not renewed while it waited. Nothing shows it
	 * as an error: the page keeps what it shows, and `Session` speaks for the session once
	 */
	get quiet(): boolean {
		return this.#code === Refusal.QUIET;
	}

	static QUIET = 'SESSION_ENDED';

	/** The quiet stand-in for a request that came back without a session and is not sent again */
	static quiet(from: Refusal): Refusal {
		return new Refusal(Refusal.QUIET, 'Not sent: the session ended', from.status, from.details, from.request);
	}
}
